import { isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { LANG_OPTIONS, LANG_SIGNAL, LsLogoComponent, LanguageService } from '@lyricstream/shared';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-onboarding',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, TranslateModule, LsLogoComponent],
  templateUrl: './onboarding.html',
  styleUrl: './onboarding.scss',
})
export class OnboardingComponent {
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  protected readonly langService = inject(LanguageService);
  protected readonly langOptions = LANG_OPTIONS;

  protected readonly lang = inject(LANG_SIGNAL);

  protected readonly currentStep = signal(1);
  protected readonly totalSteps = 5;

  // i18n key for current step name — e.g. "ONBOARDING.STEPS.S1.NAME"
  protected readonly stepNameKey = computed(() => `ONBOARDING.STEPS.S${this.currentStep()}.NAME`);
  protected readonly padStep = computed(() => String(this.currentStep()).padStart(2, '0'));
  protected readonly stepSegments = computed(() =>
    Array.from({ length: this.totalSteps }, (_, i) => i + 1),
  );

  goNext(): void {
    if (this.currentStep() < this.totalSteps) {
      this.currentStep.update((s) => s + 1);
    }
  }

  goPrev(): void {
    if (this.currentStep() > 1) {
      this.currentStep.update((s) => s - 1);
    }
  }

  finish(): void {
    this.router.navigate(['/', this.lang()]);
  }

  constructor() {
    afterNextRender(() => {
      if (isPlatformBrowser(this.platformId)) {
        const handler = (e: KeyboardEvent): void => {
          if (e.key === 'ArrowRight') this.goNext();
          if (e.key === 'ArrowLeft') this.goPrev();
        };
        window.addEventListener('keydown', handler);
      }
    });
  }
}
